<script lang="ts" context="module">
  import type { ComponentType, SvelteComponentTyped } from 'svelte';
  export type StartComponent = ComponentType<
    SvelteComponentTyped<{
      background: string;
    }>
  >;

  export type Tile = {
    title: string;
    url: string;
    detail: string;
    image: string;
    imageAlt: string
  };

  export type Tiles = Array<Tile>;

  type Bounds = Pick<DOMRect, 'top' | 'left' | 'width' | 'height'>;
</script>

<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import Button from './Button.svelte';
  import Buttons from './Buttons.svelte';
  import Typography from './Typography.svelte';

  export let tiles: Tiles;

  let scrollContainer: HTMLElement;
  let scrollItems: Array<HTMLLIElement> = [];
  let scrollImages: Array<HTMLImageElement> = [];

  let scrollActiveIndex: number = 0;

  let hoverActiveIndex: number | null = null;

  $: activeIndex = hoverActiveIndex !== null ? hoverActiveIndex : scrollActiveIndex;

  function updateHoverIndex(index: number): void {
    hoverActiveIndex = index;
  }

  function clearHoverIndex(e: Event): void {
    if (e.target === scrollContainer) {
      hoverActiveIndex = null;
    }
  }

  const animationTime = parseInt(window.getComputedStyle(document.documentElement).getPropertyValue('--transition'));

  let animateIndex: number | null = null;
  let animateBounds: Bounds | null = null;
  let animateStartScale: number | null = null;
  let animateStartHeight: number | null = null;
  let animation = false;
  let fullscreen = false;

  function grow(index: number): void {
    animateIndex = index;
    animateBounds = scrollImages[index].getBoundingClientRect();
    const widthRatio = animateBounds.width / window.innerWidth;
    animateStartScale = widthRatio;
    animateStartHeight = animateBounds.height / widthRatio;
    animation = true;
    setTimeout(() => (fullscreen = true), animationTime);
    setTimeout(() => (animation = false), animationTime * 2);
  }

  function shrink(): void {
    fullscreen = false;
    animation = true;
    setTimeout(() => (animateIndex = null), animationTime);
    setTimeout(() => (animation = false), animationTime * 2);
  }

  onMount(() => {
    window.addEventListener('keyup', escapeFullscreen);

    scrollContainer.addEventListener('scroll', () => {
      const containerBounds = scrollContainer.getBoundingClientRect();
      scrollItems.forEach((item, index) => {
        const itemBounds = item.getBoundingClientRect();
        if (Math.abs(itemBounds.left - containerBounds.left) < 10) {
          scrollActiveIndex = index;
        } else if (Math.abs(itemBounds.right - containerBounds.right) < 10) {
          scrollActiveIndex = index;
        }
      });
    });
  });

  onDestroy(() => {
    window.removeEventListener('keyup', escapeFullscreen);
  });

  function escapeFullscreen(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      shrink();
    }
  }
</script>

<nav in:fade bind:this={scrollContainer} on:mouseout={clearHoverIndex} on:blur={clearHoverIndex}>
  <ul>
    {#each tiles as tile, index}
      <li bind:this={scrollItems[index]} class:active={activeIndex === index}>
        <button
          on:mouseover={() => updateHoverIndex(index)}
          on:focus={() => updateHoverIndex(index)}
          on:click={() => grow(index)}
          on:keypress={() => grow(index)}
          tabindex={fullscreen ? -1 : 0}
          title={tile.title}
        >
          <img class="tile-image" alt={tile.imageAlt} role="presentation" src={tile.image} bind:this={scrollImages[index]} />
          <Typography type="h3" centred={false} display size={1.75} end={0.5}>
            {tile.title}
          </Typography>
          {#if tile.detail}
            <Typography type="h4" centred={false} size={0.9} lineHeight={1.25}>
              {tile.detail}
            </Typography>
          {/if}

          {#if animateBounds && animateIndex === index}
            <div
              style:top={`${animateBounds.top}px`}
              style:left={`${animateBounds.left}px`}
              style:height={`${animateStartHeight}px`}
              style:transform={`scale(${animateStartScale})`}
              class="tile-fullscreen-container"
              class:animation
              class:fullscreen
            >
              {#if fullscreen}
                <div class="tile-fullscreen">
                  <Typography type="h1" display size={3}>
                    Create your own {`${tile.title}`}
                  </Typography>
                  <Buttons>
                    <Button href={`/${tile.url}`}>Start Building</Button>
                  </Buttons>
                </div>
              {/if}
              <img class="tile-image-fullscreen" alt={tile.imageAlt} role="presentation" src={tile.image} />
            </div>
          {/if}
        </button>
      </li>
    {/each}
  </ul>
</nav>

<style lang="scss">
  nav {
    display: flex;
    max-width: min-content;
    height: 100%;

    overflow-x: scroll;
    scroll-snap-type: x mandatory;
    overscroll-behavior-x: none;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  nav::-webkit-scrollbar {
    display: none;
  }

  ul {
    display: flex;
    flex-direction: row;
    list-style-type: none;
    margin: 0;
    padding: 0;
  }

  li {
    scroll-snap-align: start;
    padding-inline-start: var(--layout-side-padding);
    cursor: pointer;
  }

  li:first-of-type {
    margin-inline-start: 0;
  }

  li:last-of-type {
    margin-inline-end: 0;
    padding-inline-end: var(--layout-side-padding);
  }

  button {
    appearance: none;
    background: none;
    outline: none;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;

    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .tile-image {
    /** set height and min-height so everything animates correctly */
    height: calc(var(--accordion-height) * 0.48);
    min-height: calc(var(--accordion-height) * 0.48);
    width: calc(var(--accordion-height) * 0.38);
    object-fit: cover;
    object-position: center;
    margin-block-start: calc(var(--accordion-height) * 0.01);
    margin-block-end: calc(var(--accordion-height) * 0.03);
  }

  button:focus > .tile-image {
    box-shadow: 0px 0px 0px var(--outline) var(--active);

    /* High-contrast */
    outline-color: transparent;
    outline-width: var(--outline-width);
    outline-style: dotted;
  }

  .tile-fullscreen-container {
    position: fixed;
    width: 100%;
    height: 100%;
    transform-origin: 0 0;
    display: flex;
    flex-direction: column;
  }

  .animation {
    transition: all var(--transition);
  }

  .fullscreen {
    width: var(--start-fullscreen-width);
    /** add !important to animated properties */
    top: 0 !important;
    left: 0 !important;
    height: var(--start-fullscreen-height) !important;
    transform: scaleX(1) scaleY(1) !important;
  }

  .tile-fullscreen {
    z-index: 1;
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: center;
    padding-inline: 1rem;
    color: var(--text-on-highlight);
  }

  .tile-image-fullscreen {
    position: fixed;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
</style>
